import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtFCFA } from "@/lib/api";
import { SectionTitle } from "@/components/Bits";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MapPin, Users, Star, CalendarCheck } from "lucide-react";

export default function ShortStay() {
  const { t } = useLang();
  const [logements, setLogements] = useState([]);
  const [filters, setFilters] = useState({ city: "", capacity: "", max_price: "" });

  const load = async () => {
    const query = { short_term: true, limit: 40 };
    if (filters.city) query.city = filters.city;
    if (filters.capacity) query.capacity = filters.capacity;
    if (filters.max_price) query.max_price = filters.max_price;
    const { data } = await api.get("/properties", { params: query });
    setLogements(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="bg-ivory min-h-screen">
      <section className="relative py-20">
        <img src="https://images.unsplash.com/photo-1670589953882-b94c9cb380f5?w=1920&q=85" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <SectionTitle light eyebrow={t("svc.stay.eyebrow")} title={t("svc.stay.title")} subtitle={t("svc.stay.subtitle")} />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-10">
        <div className="rounded-xl bg-white border border-black/5 p-5 mb-8 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Input placeholder={t("svc.stay.city")} value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} data-testid="stay-city" />
          <Input type="number" placeholder={t("svc.stay.guests")} value={filters.capacity} onChange={(e) => setFilters({ ...filters, capacity: e.target.value })} data-testid="stay-guests" />
          <Input type="number" placeholder={t("svc.stay.budget")} value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} data-testid="stay-budget" />
          <Button onClick={load} className="bg-navy text-white rounded-lg" data-testid="stay-search">{t("svc.stay.search")}</Button>
        </div>

        {logements.length === 0 && <p className="text-center text-slate-500 py-12">Aucun logement disponible pour ces critères.</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {logements.map((p) => (
            <Link to={`/bien/${p.id}`} key={p.id} className="rounded-xl bg-white border border-black/5 overflow-hidden card-hover group block" data-testid={`stay-card-${p.reference}`}>
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src={p.images?.[0]} alt={p.title} className="h-full w-full object-cover img-zoom" />
                {p.rating != null && (
                  <span className="absolute top-3 right-3 flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold text-navy shadow" data-testid={`stay-rating-${p.reference}`}>
                    <Star className="h-3.5 w-3.5 fill-gold text-gold" /> {p.rating}
                  </span>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-serif text-xl font-semibold text-navy group-hover:text-gold transition-colors">{p.title}</h3>
                <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {p.city}, {p.country}</p>
                <div className="mt-3 flex items-center gap-4 text-sm text-slate-600">
                  <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-gold" /> {p.capacity} {t("svc.stay.guestsWord")}</span>
                  {p.reviews_count > 0 && <span className="text-slate-400">· {p.reviews_count} avis</span>}
                </div>
                <div className="mt-4 flex items-end justify-between border-t border-dashed pt-4">
                  <span className="font-mono text-lg font-bold text-navy">{fmtFCFA(p.price_night)}<span className="text-xs text-slate-400"> {t("svc.stay.perNight")}</span></span>
                  <span className="inline-flex items-center gap-1.5 rounded-lg bg-gold px-3 py-2 text-sm font-semibold text-navy"><CalendarCheck className="h-4 w-4" /> {t("svc.stay.book")}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
